import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

// 🔹 Remplace cette URL par ton vrai logo (dans /public ou hébergé)
const LOGO_URL = '/logo.png'; // Ex: '/logo-entreprise.png' ou 'https://tonsite.com/logo.png'

interface Ligne {
  reference: string;
  designation: string;
  quantite: number;
  prix_unitaire_ht: number;
  total_ht: number;
}

interface DevisPublic {
  numero: string;
  client: { raison_sociale: string; code: string };
  commercial?: { name: string };
  date_devis: string;
  date_validite: string;
  statut: 'BROUILLON' | 'ENVOYE' | 'ACCEPTE' | 'REFUSE' | 'EXPIRE' | 'ANNULE';
  total_ttc: number;
  notes_client?: string;
  lignes: Ligne[];
}

export default function DevisPublic() {
  const { token } = useParams<{ token: string }>();
  const [devis, setDevis] = useState<DevisPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responding, setResponding] = useState(false);

  // États pour les messages de validation
  const [actionCompleted, setActionCompleted] = useState<'accept' | 'reject' | null>(null);

  // Modal refus avec motif
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [motifRefus, setMotifRefus] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Lien invalide ou manquant.');
      setLoading(false);
      return;
    }

    getResources(`devis-bcc/public/${token}`)
      .then((res) => {
        setDevis(res.data.data || res.data);
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'Ce devis est invalide, expiré ou déjà traité.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const acceptDevis = async () => {
    setResponding(true);
    try {
      await createResource(`devis-bcc/accept/${token}`,{});
      setActionCompleted('accept');
      toast.success('Devis accepté avec succès !');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'acceptation');
    } finally {
      setResponding(false);
    }
  };

  const rejectDevis = async () => {
    if (!motifRefus.trim()) {
      toast.error('Veuillez indiquer un motif pour le refus');
      return;
    }

    setResponding(true);
    try {
      await createResource(`devis-bcc/reject/${token}`, {
        motif: motifRefus.trim(),
      });
      setActionCompleted('reject');
      toast.success('Devis refusé. Merci pour votre retour.');
      setShowRejectModal(false);
      setMotifRefus('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors du refus');
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (error || !devis) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl p-12 text-center">
          <i className="ri-error-warning-line text-8xl text-red-500 mb-8"></i>
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Devis non disponible</h2>
          <p className="text-lg text-gray-600 mb-8">{error}</p>
          <p className="text-gray-500">Contactez votre commercial pour plus d'informations.</p>
        </div>
      </div>
    );
  }

  const totalHT = (devis.total_ttc / 1.1925).toFixed(2);
  const tva = (devis.total_ttc - devis.total_ttc / 1.1925).toFixed(2);
  const isPending = devis.statut === 'ENVOYE';
  const isExpired = new Date(devis.date_validite) < new Date() && devis.statut === 'ENVOYE';

  // === MESSAGE DE VALIDATION APRÈS ACTION ===
  if (actionCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-3xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-10 py-16 text-white text-center">
            <img src={LOGO_URL} alt="Logo entreprise" className="h-20 mx-auto mb-8" />
            <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
              <i
                className={`text-7xl ${
                  actionCompleted === 'accept' ? 'ri-check-double-line text-green-600' : 'ri-information-line text-orange-600'
                }`}
              ></i>
            </div>
            <h2 className="text-5xl font-bold mb-6">
              {actionCompleted === 'accept' ? 'Félicitations ! 🎉' : 'Merci pour votre retour'}
            </h2>
          </div>

          <div className="px-10 py-12 text-center">
            {actionCompleted === 'accept' ? (
              <>
                <h3 className="text-3xl font-bold text-gray-800 mb-6">
                  Votre devis n°{devis.numero} a été accepté !
                </h3>
                <p className="text-xl text-gray-700 leading-relaxed mb-10">
                  Nous sommes ravis de collaborer avec <strong>{devis.client.raison_sociale}</strong>.<br />
                  Le montant total de <strong>{devis.total_ttc.toLocaleString('fr-FR')} GNF TTC</strong> est confirmé.
                </p>

                <div className="bg-green-50 rounded-2xl p-8 mb-10 max-w-2xl mx-auto">
                  <h4 className="text-2xl font-semibold text-green-900 mb-6">Prochaines étapes</h4>
                  <ul className="text-left text-lg text-gray-700 space-y-4">
                    <li className="flex items-start gap-4">
                      <i className="ri-file-list-3-line text-green-600 text-2xl"></i>
                      <span>Préparation de votre bon de commande</span>
                    </li>
                    <li className="flex items-start gap-4">
                      <i className="ri-mail-send-line text-blue-600 text-2xl"></i>
                      <span>Envoi d'une confirmation officielle par email</span>
                    </li>
                    <li className="flex items-start gap-4">
                      <i className="ri-phone-line text-purple-600 text-2xl"></i>
                      <span>Contact de votre commercial pour les détails</span>
                    </li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-3xl font-bold text-gray-800 mb-6">
                  Devis n°{devis.numero} refusé
                </h3>
                <p className="text-xl text-gray-700 leading-relaxed mb-10">
                  Merci d’avoir pris le temps de nous répondre.<br />
                  Votre retour nous aide à améliorer nos offres futures.
                </p>

                <div className="bg-orange-50 rounded-2xl p-8 mb-10 max-w-2xl mx-auto">
                  <p className="text-lg italic text-gray-700 mb-4">
                    "{motifRefus}"
                  </p>
                  <p className="text-gray-600">
                    Nous restons à votre disposition pour une nouvelle proposition adaptée à vos besoins.
                  </p>
                </div>
              </>
            )}

            <div className="text-center space-y-6">
              <p className="text-gray-600 text-lg">
                Une question ? Contactez-nous :<br />
                <strong>commercial@tonentreprise.com</strong> • <strong>+224 XX XXX XXX</strong>
              </p>

              <a
                href="https://tonentreprise.com"
                className="inline-flex items-center gap-3 px-10 py-5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xl rounded-2xl shadow-xl transition transform hover:scale-105"
              >
                <i className="ri-home-line"></i>
                Retour au site
              </a>
            </div>
          </div>

          <div className="bg-gray-100 px-10 py-6 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Ton Entreprise - Tous droits réservés
            </p>
          </div>
        </div>
      </div>
    );
  }

  // === PAGE PRINCIPALE DU DEVIS ===
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header avec logo */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-10 py-12 text-white">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-8">
                <img src={LOGO_URL} alt="Logo entreprise" className="h-20" />
                <div>
                  <h1 className="text-5xl font-bold">DEVIS</h1>
                  <p className="text-3xl opacity-90 mt-2">N° {devis.numero}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl opacity-90">Émis le</p>
                <p className="text-3xl font-bold">
                  {new Date(devis.date_devis).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </div>

          {/* Infos client & validité */}
          <div className="px-10 py-10 grid grid-cols-1 md:grid-cols-2 gap-10 bg-gray-50">
            <div className="bg-white rounded-2xl p-8 shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Client</h3>
              <p className="text-lg"><span className="font-medium">Raison sociale :</span> {devis.client.raison_sociale}</p>
              <p className="text-lg mt-3"><span className="font-medium">Code :</span> {devis.client.code}</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">Validité</h3>
              <p className="text-lg">
                <span className="font-medium">Valable jusqu'au :</span>{' '}
                <span className={isExpired ? 'text-red-600 font-bold' : 'font-medium'}>
                  {new Date(devis.date_validite).toLocaleDateString('fr-FR')}
                </span>
                {isExpired && <span className="ml-3 text-red-600 font-bold">(EXPIRÉ)</span>}
              </p>
              {devis.commercial && (
                <p className="text-lg mt-4">
                  <span className="font-medium">Commercial :</span> {devis.commercial.name}
                </p>
              )}
            </div>
          </div>

          {/* Tableau lignes */}
          <div className="px-10 py-10">
            <h3 className="text-2xl font-semibold text-gray-800 mb-8">Détail des prestations</h3>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-200">
                  <tr>
                    <th className="px-8 py-5 text-left text-sm font-semibold text-gray-700">Référence</th>
                    <th className="px-8 py-5 text-left text-sm font-semibold text-gray-700">Désignation</th>
                    <th className="px-8 py-5 text-center text-sm font-semibold text-gray-700">Qté</th>
                    <th className="px-8 py-5 text-right text-sm font-semibold text-gray-700">Prix HT</th>
                    <th className="px-8 py-5 text-right text-sm font-semibold text-gray-700">Total HT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {devis.lignes.map((ligne, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition">
                      <td className="px-8 py-5 text-gray-600">{ligne.produit?.reference || '—'}</td>
                      <td className="px-8 py-5 text-gray-800">{ligne.produit?.designation}</td>
                      <td className="px-8 py-5 text-center text-gray-800">{ligne.quantite}</td>
                      <td className="px-8 py-5 text-right text-gray-800">
                        {Number(ligne.prix_unitaire_ht).toLocaleString('fr-FR')} GNF
                      </td>
                      <td className="px-8 py-5 text-right font-semibold text-gray-900">
                        {Number(ligne.total_ht).toLocaleString('fr-FR')} GNF
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totaux */}
            <div className="mt-12 max-w-lg ml-auto bg-gradient-to-b from-blue-50 to-indigo-50 rounded-3xl p-10 shadow-xl border border-blue-200">
              <div className="space-y-5 text-xl">
                <div className="flex justify-between">
                  <span className="text-gray-700">Total HT</span>
                  <span className="font-bold">{Number(totalHT).toLocaleString('fr-FR')} GNF</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">TVA (19.25%)</span>
                  <span className="font-bold">{Number(tva).toLocaleString('fr-FR')} GNF</span>
                </div>
                <div className="border-t-4 border-indigo-600 pt-6 flex justify-between text-3xl font-bold text-indigo-700">
                  <span>Total TTC</span>
                  <span>{devis.total_ttc.toLocaleString('fr-FR')} GNF</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {devis.notes_client && (
              <div className="mt-12 p-8 bg-amber-50 rounded-2xl border border-amber-300">
                <h4 className="text-xl font-bold text-amber-900 mb-4">Notes et conditions</h4>
                <p className="text-gray-700 whitespace-pre-line text-lg">{devis.notes_client}</p>
              </div>
            )}
          </div>

          {/* Boutons d'action ou état final */}
          <div className="px-10 py-12 bg-gray-50 text-center border-t">
            {isPending && !isExpired && (
              <div className="flex flex-col sm:flex-row gap-8 justify-center">
                <button
                  onClick={acceptDevis}
                  disabled={responding}
                  className="px-14 py-6 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-bold text-2xl rounded-3xl shadow-2xl transition transform hover:scale-105"
                >
                  {responding ? 'Traitement en cours...' : 'Accepter le devis'}
                </button>

                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={responding}
                  className="px-14 py-6 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-bold text-2xl rounded-3xl shadow-2xl transition transform hover:scale-105"
                >
                  Refuser le devis
                </button>
              </div>
            )}

            {(devis.statut !== 'ENVOYE' || isExpired) && !actionCompleted && (
              <div className="max-w-2xl mx-auto py-10">
                <div className="bg-gray-100 rounded-3xl p-10 shadow-xl">
                  <i className={`text-8xl mx-auto block mb-6 ${isExpired ? 'text-orange-600 ri-time-line' : 'text-blue-600 ri-information-line'}`}></i>
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">
                    {isExpired ? 'Devis expiré' : 'Devis déjà traité'}
                  </h3>
                  <p className="text-xl text-gray-600">
                    {isExpired
                      ? 'La date de validité est dépassée.'
                      : 'Ce devis a déjà été accepté ou refusé.'}
                  </p>
                  <p className="text-gray-500 mt-6">Contactez votre commercial pour plus d'informations.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 text-center text-gray-500">
          <p className="text-sm">© {new Date().getFullYear()} Ton Entreprise - Tous droits réservés</p>
        </div>
      </div>

      {/* Modal Refus avec motif */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-10">
            <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">Refuser le devis</h3>
            <p className="text-lg text-gray-600 mb-8 text-center">
              Merci de nous indiquer la raison de votre refus. Cela nous aide à améliorer nos offres.
            </p>

            <textarea
              value={motifRefus}
              onChange={(e) => setMotifRefus(e.target.value)}
              placeholder="Exemple : Prix trop élevé • Délai trop long • Besoin modifié • Autre concurrent • ..."
              rows={6}
              className="w-full px-6 py-4 border-2 border-gray-300 rounded-2xl focus:outline-none focus:border-red-500 resize-none text-lg"
              autoFocus
            />

            <div className="flex gap-6 mt-10 justify-center">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setMotifRefus('');
                }}
                className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold text-lg rounded-2xl hover:bg-gray-100 transition"
              >
                Annuler
              </button>
              <button
                onClick={rejectDevis}
                disabled={responding || !motifRefus.trim()}
                className="px-10 py-4 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-bold text-xl rounded-2xl shadow-xl transition transform hover:scale-105"
              >
                {responding ? 'Envoi...' : 'Confirmer le refus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}